export function ApiDocs() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">

          {/* Header */}
          <div>
            <h1 className="text-heading font-bold text-gray-900 mb-4">Intone API Documentation</h1>
            <div className="h-1 w-20 bg-blue-600 mb-6"></div>
          </div>

          {/* Overview */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Overview</h2>
            <p className="text-gray-700 leading-relaxed">
              The Intone API provides programmatic access to manage AI voice agents, initiate calls, and manage CRM data.
              This RESTful API uses JSON for request and response bodies.
            </p>
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <p className="font-mono text-body"><strong>Base URL:</strong> <code className="text-blue-600">https://api.calls.intone.io</code></p>
              <p className="font-mono text-body mt-1"><strong>API Version:</strong> v1</p>
            </div>
          </section>

          {/* Authentication */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Authentication</h2>
            <p className="text-gray-700">
              All API endpoints require authentication using a Bearer token in the <code className="bg-gray-100 px-2 py-1 rounded text-body">Authorization</code> header:
            </p>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
              <code>Authorization: Bearer YOUR_API_TOKEN_HERE</code>
            </pre>

            <h3 className="text-heading font-semibold text-gray-800 mt-6">Obtaining an API Token</h3>
            <p className="text-gray-700">
              Contact your Intone administrator to generate an API token for your account.
            </p>

            <h3 className="text-heading font-semibold text-gray-800 mt-6">Testing Your Token</h3>
            <p className="text-gray-700">
              Use the <code className="bg-gray-100 px-2 py-1 rounded text-body">/v1/platform/me</code> endpoint to verify your token is working correctly.
            </p>
          </section>

          {/* Rate Limits */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Rate Limits</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Standard tier: 100 requests per minute</li>
              <li>Contact support for higher limits</li>
            </ul>
          </section>

          {/* Error Handling */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Error Handling</h2>
            <p className="text-gray-700">The API uses standard HTTP status codes:</p>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-body font-semibold text-gray-900">Status Code</th>
                    <th className="px-4 py-3 text-left text-body font-semibold text-gray-900">Meaning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  <tr><td className="px-4 py-3 text-body font-mono">200</td><td className="px-4 py-3 text-body text-gray-700">Success</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">400</td><td className="px-4 py-3 text-body text-gray-700">Bad Request - Invalid parameters</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">401</td><td className="px-4 py-3 text-body text-gray-700">Unauthorized - Invalid or missing token</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">403</td><td className="px-4 py-3 text-body text-gray-700">Forbidden - Insufficient permissions</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">404</td><td className="px-4 py-3 text-body text-gray-700">Not Found - Resource doesn't exist</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">422</td><td className="px-4 py-3 text-body text-gray-700">Unprocessable Entity - Validation error</td></tr>
                  <tr><td className="px-4 py-3 text-body font-mono">500</td><td className="px-4 py-3 text-body text-gray-700">Internal Server Error</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-heading font-semibold text-gray-800 mt-6">Error Response Format</h3>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
              <code>{`{
  "detail": "Error message describing what went wrong"
}`}</code>
            </pre>
          </section>

          {/* Endpoints */}
          <section className="space-y-6">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Endpoints</h2>

            {/* Authentication Endpoint */}
            <div className="space-y-4">
              <h3 className="text-heading font-semibold text-gray-800">Authentication</h3>

              <div className="pl-4 border-l-4 border-blue-500 space-y-3">
                <h4 className="text-heading font-semibold text-gray-900">Test Authentication</h4>
                <p className="font-mono text-blue-600 text-heading">GET /v1/platform/me</p>
                <p className="text-gray-700">Verify your API token and retrieve information about the authenticated user.</p>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Response:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>{`{
  "authenticated": true,
  "company_id": 42,
  "superadmin": false
}`}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Call Management */}
            <div className="space-y-4">
              <h3 className="text-heading font-semibold text-gray-800 mt-8">Call Management</h3>

              {/* Initiate Call */}
              <div className="pl-4 border-l-4 border-green-500 space-y-3">
                <h4 className="text-heading font-semibold text-gray-900">Initiate Call</h4>
                <p className="font-mono text-green-600 text-heading">POST /v1/initiate_call</p>
                <p className="text-gray-700">Initiate an outbound call with an AI agent. The platform will place the call immediately.</p>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Request Body:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>{`{
  "campaign_id": 123,
  "from_number": "15551234567",
  "to_number": "15559876543",
  "data": {
    "customer_name": "John Doe",
    "account_number": "ACC-12345"
  },
  "metadata": {
    "external_id": "your-reference-id"
  },
  "agent_id": "auto",
  "voice_id": "auto"
}`}</code>
                  </pre>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="font-semibold text-blue-900 mb-2">Required Fields:</p>
                  <ul className="list-disc list-inside text-blue-800 space-y-1 text-body">
                    <li><code className="bg-blue-100 px-2 py-1 rounded">campaign_id</code> (integer) - Your campaign identifier</li>
                    <li><code className="bg-blue-100 px-2 py-1 rounded">to_number</code> (string) - Phone number to call (11 digits)</li>
                  </ul>
                </div>

                <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                  <p className="font-semibold text-gray-900 mb-2">Optional Fields:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 text-body">
                    <li><code className="bg-gray-200 px-2 py-1 rounded">from_number</code> (string) - Caller ID number</li>
                    <li><code className="bg-gray-200 px-2 py-1 rounded">data</code> (object|null) - Custom data for AI agent. Can be <code>null</code></li>
                    <li><code className="bg-gray-200 px-2 py-1 rounded">metadata</code> (object|null) - Additional metadata. Can be <code>null</code></li>
                    <li><code className="bg-gray-200 px-2 py-1 rounded">agent_id</code> (string) - Specific agent (default: "auto")</li>
                    <li><code className="bg-gray-200 px-2 py-1 rounded">voice_id</code> (string) - Specific voice (default: "auto")</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Response:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>{`{
  "call_id": "call_abc123xyz",
  "agent_id": "agent_001",
  "voice_id": "voice_rachel",
  "experiment_tag": "variant_a",
  "company_id": 42,
  "campaign_id": 123
}`}</code>
                  </pre>
                </div>
              </div>

              {/* Register Call */}
              <div className="pl-4 border-l-4 border-green-500 space-y-3 mt-6">
                <h4 className="text-heading font-semibold text-gray-900">Register Call</h4>
                <p className="font-mono text-green-600 text-heading">POST /v1/register_call</p>
                <p className="text-gray-700">
                  Register an inbound call or prepare configuration without initiating it. Returns call configuration and SIP connection details.
                </p>
                <p className="text-gray-600 text-body italic">Request body is similar to /v1/initiate_call. Response includes <code>sip_url</code> field.</p>
              </div>
            </div>

            {/* CRM Management */}
            <div className="space-y-4 mt-8">
              <h3 className="text-heading font-semibold text-gray-800">CRM Management</h3>

              {/* Get Lists */}
              <div className="pl-4 border-l-4 border-purple-500 space-y-3">
                <h4 className="text-heading font-semibold text-gray-900">Get Lists</h4>
                <p className="font-mono text-purple-600 text-heading">GET /v1/platform/lists</p>
                <p className="text-gray-700">Retrieve CRM lists for your campaigns.</p>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Query Parameters:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 text-body">
                    <li><code className="bg-gray-100 px-2 py-1 rounded">campaign_id</code> (optional) - Filter by campaign ID</li>
                    <li><code className="bg-gray-100 px-2 py-1 rounded">active</code> (optional) - Filter by active status</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Example Request:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>GET /v1/platform/lists?campaign_id=123&active=true</code>
                  </pre>
                </div>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Response:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>{`[
  {
    "id": 1,
    "name": "Q4 Prospects",
    "company_id": 42,
    "campaign_id": 123,
    "is_active": true,
    "size": 5432
  }
]`}</code>
                  </pre>
                </div>
              </div>

              {/* Get Leads */}
              <div className="pl-4 border-l-4 border-purple-500 space-y-3 mt-6">
                <h4 className="text-heading font-semibold text-gray-900">Get Leads</h4>
                <p className="font-mono text-purple-600 text-heading">GET /v1/platform/leads</p>
                <p className="text-gray-700">Retrieve leads from a specific CRM list with pagination.</p>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Query Parameters:</p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 text-body">
                    <li><code className="bg-gray-100 px-2 py-1 rounded">list_id</code> (required) - CRM list ID</li>
                    <li><code className="bg-gray-100 px-2 py-1 rounded">page</code> (optional) - Page number (default: 1)</li>
                    <li><code className="bg-gray-100 px-2 py-1 rounded">page_size</code> (optional) - Leads per page (default/max: 10000)</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Example Request:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>GET /v1/platform/leads?list_id=1&page=1&page_size=100</code>
                  </pre>
                </div>

                <div>
                  <p className="font-semibold text-gray-800 mb-2">Response:</p>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                    <code>{`[
  {
    "id": "lead_abc123",
    "list_id": 1,
    "phone_number": "+15551234567",
    "status": "new",
    "data": {
      "name": "John Doe",
      "email": "john@example.com"
    },
    "calls_made": 0
  }
]`}</code>
                  </pre>
                </div>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Code Examples</h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-heading font-semibold text-gray-800 mb-3">Python</h3>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                  <code>{`import requests

BASE_URL = "https://api.calls.intone.io"
API_TOKEN = "your_api_token_here"

headers = {
    "Authorization": f"Bearer {API_TOKEN}",
    "Content-Type": "application/json"
}

# Test authentication
response = requests.get(f"{BASE_URL}/v1/platform/me", headers=headers)
print("Auth test:", response.json())

# Initiate a call
call_data = {
    "campaign_id": 123,
    "to_number": "15551234567",
    "data": {"customer_name": "John Doe"}
}

response = requests.post(
    f"{BASE_URL}/v1/initiate_call",
    headers=headers,
    json=call_data
)

if response.status_code == 200:
    print(f"Call initiated: {response.json()['call_id']}")
else:
    print(f"Error: {response.json()}")`}</code>
                </pre>
              </div>

              <div>
                <h3 className="text-heading font-semibold text-gray-800 mb-3">cURL</h3>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-body">
                  <code>{`# Test authentication
curl -X GET "https://api.calls.intone.io/v1/platform/me" \\
  -H "Authorization: Bearer YOUR_TOKEN"

# Initiate a call
curl -X POST "https://api.calls.intone.io/v1/initiate_call" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "campaign_id": 123,
    "to_number": "15551234567",
    "data": {"customer_name": "John Doe"}
  }'`}</code>
                </pre>
              </div>
            </div>
          </section>

          {/* Data Types */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Data Types</h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-heading font-semibold text-gray-800">Phone Numbers</h3>
                <p className="text-gray-700 mt-2">Phone numbers must be provided as strings:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2 ml-4">
                  <li><strong>11 digits</strong> (recommended): <code className="bg-gray-100 px-2 py-1 rounded">"15551234567"</code></li>
                  <li><strong>10 digits</strong> (US only): <code className="bg-gray-100 px-2 py-1 rounded">"5551234567"</code> (auto-prefixed with "1")</li>
                </ul>
              </div>

              <div>
                <h3 className="text-heading font-semibold text-gray-800">Timestamps</h3>
                <p className="text-gray-700 mt-2">
                  All timestamps are in ISO 8601 format: <code className="bg-gray-100 px-2 py-1 rounded">"2024-03-20T15:30:00"</code>
                </p>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Best Practices</h2>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Store API tokens securely</strong> - Never commit tokens to version control</li>
              <li><strong>Handle errors gracefully</strong> - Always check response status codes</li>
              <li><strong>Use pagination</strong> - For large datasets, use the pagination parameters</li>
              <li><strong>Validate phone numbers</strong> - Ensure proper format before requests</li>
              <li><strong>Monitor rate limits</strong> - Implement exponential backoff for retries</li>
              <li><strong>Use meaningful metadata</strong> - Include tracking IDs in the metadata field</li>
              <li><strong>Test with /me endpoint</strong> - Verify authentication first</li>
              <li><strong>Handle null values</strong> - The data and metadata fields accept null</li>
            </ol>
          </section>

          {/* Support */}
          <section className="space-y-4">
            <h2 className="text-heading font-semibold text-gray-900 border-b pb-2">Support</h2>
            <p className="text-gray-700">For API support, questions, or to request additional features:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Email:</strong> <a href="mailto:info@intone.io" className="text-blue-600 hover:underline">info@intone.io</a></li>
              <li><strong>Website:</strong> <a href="https://www.intone.io" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">www.intone.io</a></li>
            </ul>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t border-gray-200">
            <p className="text-body text-gray-500 italic">Last updated: October 2025</p>
          </div>

        </div>
      </div>
    </div>
  )
}
